import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Clock } from 'lucide-react';
import { bylineFor, formatInsightDate, readingTimeFor, topicFor } from '@/data/insights';
import { Reveal } from '../Reveal';

// Shared article card — used by the Insights hub grid and related-articles
// rails. Consistent 16/10 image ratio, subtle topic-colour accent, balanced
// heights, restrained hover movement, single clear interactive area.
export const InsightCard = ({ article, index = 0 }) => {
  const topic = topicFor(article.primaryTopic);
  const secondary = article.secondaryTopics?.length ? topicFor(article.secondaryTopics[0]) : null;
  const reading = readingTimeFor(article);

  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <Link
        to={`/insights/${article.slug}`}
        data-testid={`insight-card-${article.slug}`}
        aria-label={`Read insight: ${article.title}`}
        className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift"
        style={topic ? { borderTopWidth: '3px', borderTopColor: topic.color } : undefined}
      >
        {article.image?.src && (
          <div className="overflow-hidden">
            <img
              src={article.image.src}
              alt={article.image?.alt || article.title}
              width={article.image.width || 1080}
              height={article.image.height || 675}
              loading="lazy"
              className="aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        )}
        <div className="flex flex-1 flex-col p-6">
          <div className="flex flex-wrap items-center gap-2">
            {topic && (
              <span
                className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide"
                style={{ backgroundColor: topic.soft, color: topic.dark }}
              >
                {topic.label}
              </span>
            )}
            {secondary && (
              <span className="rounded-full border border-slate-200 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                {secondary.label}
              </span>
            )}
          </div>
          <h3 className="mt-4 text-lg font-extrabold leading-snug tracking-tight text-navy-900 transition-colors group-hover:text-navy-600">
            {article.title}
          </h3>
          <p className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-slate-600">{article.excerpt}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-medium text-slate-500">
            <span>{bylineFor(article)}</span>
            <time dateTime={article.publishedAt} className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              {formatInsightDate(article.publishedAt)}
            </time>
            {article.updatedAt && (
              <span>
                Updated{' '}
                <time dateTime={article.updatedAt}>{formatInsightDate(article.updatedAt)}</time>
              </span>
            )}
            {reading && (
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {reading} min read
              </span>
            )}
          </div>
          <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-bold text-navy-900">
            Read Insight
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </Reveal>
  );
};
